import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../translations/LanguageContext';
import { services } from '../mock/mockData';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);

  const serviceTranslations = {
    'acupuncture': t.services.acupuncture,
    'aculifting': t.services.aculifting,
    'korean-relaxation': t.services.koreanRelaxation
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-4'
      }`}
    >
      <nav className="nav-header mx-4 md:mx-6">
        <div className="flex items-center justify-between w-full px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_traditional-medicine-3/artifacts/f5y9qdpg_Logo.png" 
              alt="Tranquil Acupuncture" 
              className="h-14 md:h-16 w-auto"
              style={{ mixBlendMode: 'darken', background: 'transparent' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link to="/" className="nav-link">{t.nav.home}</Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesDropdownOpen(true)}
              onMouseLeave={() => setIsServicesDropdownOpen(false)}
            >
              <button className="nav-link flex items-center gap-1">
                {t.nav.services}
                <ChevronDown size={16} className={`transition-transform ${isServicesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isServicesDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-64">
                  <div className="bg-white rounded-xl shadow-lg border border-border-light overflow-hidden">
                    {services.map((service) => {
                      const translation = serviceTranslations[service.id];
                      return (
                        <Link
                          key={service.id}
                          to={`/services/${service.id}`}
                          className="block px-4 py-3 hover:bg-accent-wash transition-colors border-b border-border-light last:border-0"
                        >
                          <div className="font-semibold text-primary">{translation.name}</div>
                          <div className="text-sm text-muted line-clamp-1">{translation.shortDesc}</div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <Link to="/#about" onClick={() => scrollToSection('about')} className="nav-link">{t.nav.about}</Link>
            <Link to="/contact" className="nav-link">{t.nav.contact}</Link>
          </div>

          {/* Language Switcher & CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <button 
              onClick={() => scrollToSection('booking')} 
              className="btn-primary"
            >
              {t.nav.bookAppointment}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 px-4 flex flex-col space-y-3">
            <Link to="/" className="nav-link text-left" onClick={() => setIsMobileMenuOpen(false)}>{t.nav.home}</Link>
            
            {/* Mobile Services Submenu */}
            <div className="space-y-2">
              <div className="nav-link text-left font-semibold">{t.nav.services}</div>
              <div className="pl-4 space-y-2">
                {services.map((service) => {
                  const translation = serviceTranslations[service.id];
                  return (
                    <Link
                      key={service.id}
                      to={`/services/${service.id}`}
                      className="block text-sm text-muted hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {translation.name}
                    </Link>
                  );
                })}
              </div>
            </div>
            
            <button onClick={() => scrollToSection('about')} className="nav-link text-left">{t.nav.about}</button>
            <Link to="/contact" className="nav-link text-left" onClick={() => setIsMobileMenuOpen(false)}>{t.nav.contact}</Link>
            <div className="pt-2">
              <LanguageSwitcher />
            </div>
            <button 
              onClick={() => scrollToSection('booking')} 
              className="btn-primary w-full mt-2"
            >
              {t.nav.bookAppointment}
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
